import React from 'react';
import {defineMessages} from 'react-intl';
import {SortOrder} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {ViewType} from '@igp/shared/redux/view';

import {useBoAgentAccessCheck, withAccessCheck} from '@access-control';
import type {RepaymentLogColumns, RepaymentLogViewModelKeys} from '@features/repayment-log';
import {
    AddRepaymentLogButton,
    editRepaymentLogsResource,
    RepaymentLogDataGrid,
    RepaymentLogDownloadCsvButton,
    repaymentLogMappingKeys,
    RepaymentLogQueryFilter,
    repaymentLogsPageResource,
    useRepaymentLogs,
} from '@features/repayment-log';

const localized = defineMessages({
    title: {
        id: 'RepaymentLogs_title',
        defaultMessage: 'Repayment Log',
    },
});

function RepaymentLogsInner() {
    const mapper = useAutoMapper();
    const viewType: ViewType = 'RepaymentLogs';
    const isEditAvailable = useBoAgentAccessCheck(editRepaymentLogsResource);
    const notDownloadableColumns: RepaymentLogColumns[] = ['edit', 'remove'];
    const columns: RepaymentLogColumns[] = [
        'repaymentDate',
        'masterAgentUid',
        'masterAgentUsername',
        'fromAgentUid',
        'fromAgentUsername',
        'repaymentAmount',
        ...((isEditAvailable ? ['edit', 'remove'] : []) as RepaymentLogColumns[]),
    ];
    const fields = [
        ...new Set(
            columns.flatMap(column =>
                mapper.map<RepaymentLogColumns, RepaymentLogViewModelKeys[]>(
                    column,
                    repaymentLogMappingKeys.columns,
                    repaymentLogMappingKeys.viewModelsKeys
                )
            )
        ),
    ];
    const {
        items,
        rawItems,
        totalCount,
        filterString,
        searchFilter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = useRepaymentLogs({
        viewType,
        fields,
        defaultPaging: {page: 1, pageSize: 25},
        defaultSorting: [
            {
                field: 'repaymentDate',
                sort: SortOrder.Desc,
            },
        ],
        validateFilter: () => true,
        syncWithUrl: true,
        realtimeMode: RealtimeUpdatesMode.Silent,
    });
    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <>
                            <AddRepaymentLogButton />
                            <RepaymentLogDownloadCsvButton
                                columns={columns.filter(column => !notDownloadableColumns.includes(column))}
                                filter={filterString}
                                filename={localized.title}
                                total={totalCount}
                                mode="server"
                            />
                        </>
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems primary>
                            <RepaymentLogQueryFilter
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['masterAgentUid_agentUid', 'repaymentDate']}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <RepaymentLogDataGrid
                    rows={items}
                    rawModels={rawItems}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    paging={{
                        ...searchFilter.paging,
                        rowCount: totalCount,
                    }}
                    columns={columns}
                    sortModel={{sortModel: searchFilter.sorting}}
                    onSortChange={handleSortChange}
                />
            }
        />
    );
}

const RepaymentLogsWithAccess = withAccessCheck(RepaymentLogsInner, repaymentLogsPageResource);

export const RepaymentLogs = withTraceErrorBoundary(RepaymentLogsWithAccess, ErrorModal);
